/**
 * Word document manipulation utilities
 */

import { PlaceholderFormat } from "../types";
import { ZERO_WIDTH_SPACE, PLACEHOLDER_REGEX } from "../constants";
import { validatePlaceholderSyntax, validateFormat } from "./validation";

/**
 * Inserts a placeholder into the Word document at the current selection
 */
export const insertPlaceholderIntoDocument = async (
  key: string,
  format?: PlaceholderFormat,
  fieldType?: string
): Promise<{ success: boolean; message: string }> => {
  try {
    // Validate key before insertion
    if (!key || typeof key !== "string" || key.trim().length === 0) {
      return { success: false, message: "Алдаа: Түлхүүр хоосон байна" };
    }

    // Validate key format
    if (!PLACEHOLDER_REGEX.VALID_KEY.test(key.trim())) {
      return { success: false, message: "Алдаа: Түлхүүрийн нэр буруу байна" };
    }

    // Validate format if provided
    if (format && format !== "default") {
      const formatValidation = validateFormat(format, fieldType as any);
      if (!formatValidation.valid) {
        return {
          success: false,
          message: `Алдаа: ${formatValidation.error || "Буруу формат"}`,
        };
      }
    }

    await Word.run(async (context) => {
      const selection = context.document.getSelection();

      // Build placeholder text with pipe syntax if format specified
      const placeholderText =
        format && format !== "default" ? `{{${key}|${format}}}` : `{{${key}}}`;

      // Validate the final placeholder syntax
      const syntaxValidation = validatePlaceholderSyntax(placeholderText);
      if (!syntaxValidation.valid) {
        throw new Error(syntaxValidation.error || "Буруу синтакс");
      }

      // Insert placeholder in a single atomic operation to prevent XML fragmentation
      const marker = ZERO_WIDTH_SPACE;

      // Insert marker first
      const range = selection.insertText(marker, Word.InsertLocation.replace);

      console.log("Inserting placeholder:", placeholderText);

      // Replace marker with placeholder text in a single operation
      range.insertText(placeholderText, Word.InsertLocation.replace);

      // Load and set font properties to ensure consistent formatting
      range.font.load("name,size,color,bold,italic");
      await context.sync();

      // Apply consistent formatting to prevent fragmentation
      range.font.name = range.font.name || "Arial";
      range.font.size = range.font.size || 12;
      range.font.color = range.font.color || "#000000";
      range.font.bold = false;
      range.font.italic = false;

      await context.sync();
    });

    // Trigger usage count update
    window.dispatchEvent(new Event("onehr:usage-updated"));

    return { success: true, message: `Орууллаа: {{${key}}}` };
  } catch (error) {
    console.error("Error inserting placeholder:", error);
    const errorMessage = error instanceof Error ? error.message : "Тодорхойгүй алдаа";
    return { success: false, message: `Алдаа: ${errorMessage}` };
  }
};
